<template>
  <div>
    <!-- Expenses Title and Filters -->
    <b-card
      no-body
      class="mb-0"
    >
      <b-card-header class="bg-primary text-white pt-50 pb-50">
        <h4 class="mb-0 text-white">
          Expenses
        </h4>
      </b-card-header>

      <!-- Error Alert -->
      <b-alert
        v-if="loadingError"
        variant="danger"
        show
        dismissible
        class="m-1 mb-0"
        @dismissed="loadingError = null"
      >
        <div class="alert-body">
          <feather-icon
            icon="AlertCircleIcon"
            class="mr-50"
          />
          <span>{{ loadingError }}</span>
        </div>
      </b-alert>

      <!-- Filters Section -->
      <b-card-body class="py-25">
        <b-row class="align-items-center">
          <!-- Date Range Filter -->
          <b-col
            cols="12"
            md="3"
            class="mb-0 mb-md-0"
          >
            <flat-picker
              v-model="dateRange"
              placeholder="Select Date Range"
              :config="{ mode: 'range' }"
              @on-close="fetchExpenses"
            />
          </b-col>

          <!-- Expense Category Filter -->
          <b-col
            cols="12"
            md="2"
            class="mb-0 mb-md-0 mt-25 mt-md-0"
          >
            <custom-v-select
              v-model="filters.expense_category"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="expenseTypeOptions"
              :reduce="option => option.value"
              label="text"
              placeholder="Expense Type"
              multiple
              :close-on-select="false"
              :deselect-from-dropdown="true"
              @input="fetchExpenses"
            />
          </b-col>
          <!-- Total Display -->
          <b-col
            cols="12"
            md="2"
            class="mb-0 mb-md-0 mt-25 px-0 mt-md-0"
          >
            <div class="total-expense-display">
              <span class="total-label">Total:</span>
              <span class="total-amount">₹{{ totalAmount }}</span>
            </div>
          </b-col>

          <!-- Created By Filter -->
          <b-col
            cols="12"
            md="2"
            class="mb-0 mb-md-0 mt-25 mt-md-0"
          >
            <custom-v-select
              v-model="filters.created_by"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="usersList"
              label="full_name"
              placeholder="Select Created By"
              multiple
              :close-on-select="false"
              :reduce="data => data.id"
              :deselect-from-dropdown="true"
              @input="fetchExpenses"
            />
          </b-col>

          <!-- Min Amount Filter -->
          <b-col
            cols="12"
            md="1"
            style="padding-right: 1px;"
            class="mb-0 mb-md-0 mt-25 mt-md-0"
          >
            <b-form-input
              v-model.number="filters.min_amount"
              type="number"
              min="0"
              size="sm"
              placeholder="Min Amount"
              @input="debouncedFiltersChange"
            />
          </b-col>

          <!-- Max Amount Filter -->
          <b-col
            cols="12"
            md="1"
            class="mb-0 mb-md-0 px-0 mt-25 mt-md-0"
          >
            <b-form-input
              v-model.number="filters.max_amount"
              type="number"
              min="0"
              size="sm"
              placeholder="Max Amount"
              @input="debouncedFiltersChange"
            />
          </b-col>

          <!-- Clear Button -->
          <b-col
            cols="12"
            md="1"
            class="d-flex align-items-center justify-content-md-end mt-25 mt-md-0"
          >
            <b-button
              v-b-tooltip.hover
              title="Clear All Filters"
              variant="outline-secondary"
              size="sm"
              class="mb-25 mb-md-0"
              @click="clearAllFilters"
            >
              Clear
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>

      <!-- Add Expense Form -->
      <b-card-body class="expense-form-wrapper">
        <b-row class="align-items-end">
          <b-col
            cols="12"
            md="3"
          >
            <b-form-input
              id="amount-input"
              ref="amountInput"
              v-model="newExpense.amt"
              type="number"
              size="sm"
              placeholder="Enter Amount"
              :disabled="isAdding"
              :class="{ 'border-danger': newExpense.amt !== null && newExpense.amt <= 0 }"
              @keydown.enter="focusExpenseType"
            />
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mt-25 mt-md-0"
          >
            <v-select
              id="expense-type-select"
              ref="expenseTypeSelect"
              v-model="newExpense.expense_type_id"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="expenseTypeOptions"
              :reduce="option => option.value"
              :disabled="isAdding"
              label="text"
              placeholder="Select Type"
              class="expense-select-sm"
              @input="focusRemark"
            />
          </b-col>
          <b-col
            cols="12"
            md="4"
            class="mt-25 mt-md-0"
          >
            <b-form-input
              id="remark-input"
              ref="remarkInput"
              v-model="newExpense.remark"
              size="sm"
              placeholder="Enter Remark"
              :disabled="isAdding"
              @keydown.enter="handleRemarkEnter"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="mt-25 mt-md-0"
          >
            <b-button
              ref="addButton"
              variant="primary"
              size="sm"
              block
              :disabled="!isValidNewExpense || isAdding"
              @click="addExpense"
            >
              <b-spinner
                v-if="isAdding"
                small
                class="mr-25"
              />
              <feather-icon
                v-else
                icon="PlusIcon"
                size="14"
                class="mr-25"
              />
              {{ isAdding ? 'Adding...' : 'Add' }}
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>

      <!-- Expenses Table -->
      <div class="table-responsive">
        <table class="table table-bordered table-sm mb-0">
          <thead>
            <tr class="bg-light">
              <th style="width: 5%;">
                Sr. No.
              </th>
              <th
                style="width: 10%;"
                class="text-right"
              >
                Amount (₹)
                <div class="font-weight-bold text-danger">
                  {{ formatAmount(totalExpense) }}
                </div>
              </th>
              <th style="width: 15%;">
                Expense Type
              </th>
              <th style="width: 25%;">
                Remark
              </th>
              <th style="width: 15%;">
                Created By
              </th>
              <th style="width: 15%;">
                Created At
              </th>
              <th style="width: 15%;">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <!-- Empty State -->
            <tr v-if="expenses.length === 0 && !loading">
              <td
                colspan="7"
                class="text-center text-muted py-2"
              >
                No expenses found
              </td>
            </tr>

            <!-- Loading State -->
            <tr v-if="loading">
              <td
                colspan="7"
                class="text-center py-2"
              >
                <b-spinner
                  small
                  class="mr-50"
                />
                Loading expenses...
              </td>
            </tr>
            <!-- Existing Expenses -->
            <tr
              v-for="expense in expenses"
              :key="expense.id"
              :class="getRowClass(expense)"
            >
              <template v-if="editingId === expense.id">
                <!-- Edit Mode -->
                <td>{{ expenses.indexOf(expense) + 1 }}</td>
                <td class="text-right">
                  <b-form-input
                    v-model.number="editingExpense.amt"
                    type="number"
                    size="sm"
                    class="text-right"
                    :disabled="isUpdating"
                    :class="{ 'border-danger': editingExpense.amt !== null && editingExpense.amt <= 0 }"
                  />
                </td>
                <td>
                  <select
                    v-model="editingExpense.expense_type_id"
                    class="form-control form-control-sm"
                    :disabled="isUpdating"
                  >
                    <option
                      v-for="option in expenseTypeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.text }}
                    </option>
                  </select>
                </td>
                <td>
                  <b-form-input
                    v-model="editingExpense.remark"
                    size="sm"
                    :disabled="isUpdating"
                  />
                </td>
                <td>{{ expense.created_by_name || '-' }}</td>
                <td>{{ expense.created_at ? formatedDate(expense.created_at, 'DD-MM-YYYY LT') : '-' }}</td>
                <td>
                  <a
                    href="javascript:void(0)"
                    class="mr-75 text-success"
                    :class="{ 'disabled-link': isUpdating }"
                    @click="isUpdating ? null : updateExpense()"
                  >
                    <feather-icon
                      icon="CheckIcon"
                      size="18"
                    />
                  </a>
                  <a
                    href="javascript:void(0)"
                    class="text-secondary"
                    :class="{ 'disabled-link': isUpdating }"
                    @click="isUpdating ? null : cancelEdit()"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="18"
                    />
                  </a>
                </td>
              </template>
              <template v-else>
                <!-- View Mode -->
                <td>{{ expenses.indexOf(expense) + 1 }}</td>
                <td class="text-right">
                  {{ formatAmount(expense.amount) }}
                </td>
                <td>{{ getExpenseTypeName(expense.expense_category) }}</td>
                <td>{{ expense.remarks }}</td>
                <td>{{ expense.created_by_name || '-' }}</td>
                <td>{{ expense.created_at ? formatedDate(expense.created_at, 'DD-MM-YYYY LT') : '-' }}</td>
                <td>
                  <a
                    href="javascript:void(0)"
                    class="mr-75 text-info"
                    :class="{ 'disabled-link': deletingId }"
                    @click="deletingId ? null : startEdit(expense)"
                  >
                    <feather-icon
                      icon="EditIcon"
                      size="18"
                    />
                  </a>
                  <a
                    href="javascript:void(0)"
                    class="text-danger"
                    :class="{ 'disabled-link': deletingId }"
                    @click="deletingId ? null : confirmDelete(expense.id)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="18"
                    />
                  </a>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
      <detailed-pagination
        v-if="!loading && !loadingError"
        :total-items="totalItems"
        :per-page="perPage"
        :current-page="currentPage"
        @page-changed="(page) => {currentPage = page}"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BSpinner,
  BAlert,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import FlatPicker from '@/components/UI/FlatPicker.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BSpinner,
    BAlert,
    FlatPicker,
    vSelect,
    CustomVSelect,
    DetailedPagination,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [dateMixin, formatMixin],
  data() {
    return {
      // Date Range - Set today's date by default
      dateRange: `${this.formatedDate(new Date(), 'YYYY-MM-DD')} to ${this.formatedDate(new Date(), 'YYYY-MM-DD')}`,

      // Filters
      filters: {
        expense_category: [],
        created_by: [],
        min_amount: null,
        max_amount: null,
      },

      // Data
      expenses: [],
      expenseTypeOptions: [],
      usersList: [],

      // Loading States
      loading: false,
      isAdding: false,
      isUpdating: false,
      deletingId: null,

      // Error State
      loadingError: null,

      // Add New Expense
      newExpense: {
        amt: null,
        expense_type_id: null,
        remark: '',
      },

      // Edit Expense
      editingId: null,
      editingExpense: {
        amt: null,
        expense_type_id: null,
        remark: '',
      },

      // pagination
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      totalAmount: 0,

      // Debounce timer
      debounceTimer: null,
    }
  },
  computed: {
    isValidNewExpense() {
      return (
        this.newExpense.amt
        && this.newExpense.amt > 0
        && this.newExpense.expense_type_id
      )
    },
    isValidEdit() {
      return (
        this.editingExpense.amt
        && this.editingExpense.amt > 0
        && this.editingExpense.expense_type_id
      )
    },
    totalExpense() {
      return this.expenses.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0)
    },
  },
  watch: {
    currentPage() {
      this.fetchExpenses()
    },
    dateRange() {
      this.currentPage = 1
      this.fetchExpenses()
    },
  },
  mounted() {
    this.fetchExpenseTypes()
    this.fetchUsersList()
    this.fetchExpenses()
    // Focus on amount input when component loads
    this.$nextTick(() => {
      this.focusAmountInput()
    })
  },
  activated() {
    // Focus on amount input when component is activated (for keep-alive components in tabs)
    this.$nextTick(() => {
      this.focusAmountInput()
    })
  },
  methods: {
    fetchData() {
      this.fetchExpenses()
    },
    // Debounced filter change
    debouncedFiltersChange() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1
        this.fetchExpenses()
      }, 500)
    },

    // Focus Management Methods
    focusAmountInput() {
      if (this.$refs.amountInput) {
        this.$refs.amountInput.focus()
      }
    },

    focusExpenseType() {
      if (this.$refs.expenseTypeSelect) {
        // Use $el to access the actual DOM element for vue-select
        const selectElement = this.$refs.expenseTypeSelect.$el.querySelector('input')
        if (selectElement) {
          selectElement.focus()
          // Open the dropdown
          this.$refs.expenseTypeSelect.$el.querySelector('.vs__dropdown-toggle').click()
        }
      }
    },

    focusRemark() {
      // Use nextTick to ensure DOM has updated after selection
      this.$nextTick(() => {
        if (this.$refs.remarkInput) {
          this.$refs.remarkInput.focus()
        }
      })
    },

    handleRemarkEnter() {
      // If form is valid, submit; otherwise focus on the first invalid field
      if (this.isValidNewExpense && !this.isAdding) {
        this.addExpense()
      } else if (!this.newExpense.amt || this.newExpense.amt <= 0) {
        this.focusAmountInput()
      } else if (!this.newExpense.expense_type_id) {
        this.focusExpenseType()
      }
    },

    // API Calls
    async fetchExpenseTypes() {
      try {
        const response = await axios.get('/operations/expense_category_options')
        // Assuming response.data is array of {id, name}
        this.expenseTypeOptions = response.data && response.data.data.map(type => ({
          value: type.id,
          text: type.name,
        }))
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to load expense types'
      }
    },

    async fetchUsersList() {
      try {
        const { data: users } = await axios.get('/delivery/users_list_by_group?group=operations_cashreg')
        this.usersList = users
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to fetch users. Please try again.'
      }
    },

    async fetchExpenses() {
      this.loading = true
      this.loadingError = null
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
        }

        if (this.dateRange) {
          const [startDate, endDate] = this.dateRange.split(' to ')
          if (startDate && endDate) {
            params.start_date = startDate
            params.end_date = endDate
          }
        }
        // Add filters
        if (this.filters.expense_category && this.filters.expense_category.length > 0) {
          params.expense_category = this.filters.expense_category
        }
        if (this.filters.created_by && this.filters.created_by.length > 0) {
          params.created_by = this.filters.created_by
        }
        if (this.filters.min_amount !== null && this.filters.min_amount !== '') {
          params.min_amount = this.filters.min_amount
        }
        if (this.filters.max_amount !== null && this.filters.max_amount !== '') {
          params.max_amount = this.filters.max_amount
        }

        const response = await axios.get(`/operations/expenses/?${getParsedQueryString(params)}`)
        this.expenses = response.data.results
        this.totalItems = response.data.count
        this.totalAmount = parseFloat(response.data.total_amount.toFixed(2))
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to load expenses. Please try again.'
        this.expenses = []
        this.totalItems = 0
      } finally {
        this.loading = false
      }
    },

    async addExpense() {
      if (!this.isValidNewExpense || this.isAdding) return

      this.isAdding = true
      this.loadingError = null
      try {
        const formData = {
          amount: this.newExpense.amt,
          expense_category: this.newExpense.expense_type_id,
          remarks: this.newExpense.remark,
        }

        await axios.post('/operations/expenses/', formData)

        await this.fetchExpenses()
        this.resetNewExpense()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Expense created successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Emit event to parent to refresh data
        this.$emit('data-updated')

        // Focus back to amount input after successful submission
        this.$nextTick(() => {
          this.focusAmountInput()
        })
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to add expense. Please try again.'
      } finally {
        this.isAdding = false
      }
    },

    async updateExpense() {
      if (!this.isValidEdit || this.isUpdating) return

      this.isUpdating = true
      this.loadingError = null
      try {
        const formData = {
          amount: this.editingExpense.amt,
          expense_category: this.editingExpense.expense_type_id,
          remarks: this.editingExpense.remark,
        }
        await axios.patch(`/operations/expenses/${this.editingId}/`, formData)

        await this.fetchExpenses()
        this.cancelEdit()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Expense updated successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Emit event to parent to refresh data
        this.$emit('data-updated')
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to update expense. Please try again.'
      } finally {
        this.isUpdating = false
      }
    },

    async deleteExpense(id) {
      this.deletingId = id
      this.loadingError = null
      try {
        await axios.delete(`/operations/expenses/${id}/`)

        await this.fetchExpenses()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Expense deleted successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Emit event to parent to refresh data
        this.$emit('data-updated')
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to delete expense. Please try again.'
      } finally {
        this.deletingId = null
      }
    },

    // Edit Operations
    startEdit(expense) {
      this.editingId = expense.id
      this.editingExpense = {
        amt: expense.amount,
        expense_type_id: expense.expense_category,
        remark: expense.remarks,
      }
    },

    cancelEdit() {
      this.editingId = null
      this.editingExpense = {
        amt: null,
        expense_type_id: null,
        remark: '',
      }
    },

    resetNewExpense() {
      this.newExpense = {
        amt: null,
        expense_type_id: null,
        remark: '',
      }
    },

    // Delete Confirmation
    confirmDelete(id) {
      // Create a custom modal with spinner support
      this.$bvModal
        .msgBoxConfirm('You won\'t be able to revert this!', {
          title: 'Are you sure?',
          size: 'sm',
          okVariant: 'danger',
          okTitle: this.deletingId === id ? 'Deleting...' : 'Yes, delete it!',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
          okDisabled: this.deletingId === id,
          cancelDisabled: this.deletingId === id,
        })
        .then(value => {
          if (value) {
            this.deleteExpense(id)
          }
        })
    },

    // Clear All Filters
    clearAllFilters() {
      this.dateRange = null
      this.filters = {
        expense_category: [],
        created_by: [],
        min_amount: null,
        max_amount: null,
      }
      this.currentPage = 1
      this.fetchExpenses()
    },

    // Helpers
    getExpenseTypeName(typeId) {
      const type = this.expenseTypeOptions.find(t => t.value === typeId)
      return type ? type.text : ''
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0'
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    validateAmount(amount) {
      if (amount === null || amount === '') return null
      return amount > 0
    },

    getRowClass(expense) {
      // You can customize row colors based on expense type name
      const typeName = this.getExpenseTypeName(expense.expense_category)
      if (typeName === 'Cash Input' || typeName === 'Loan Exp') {
        return 'table-warning'
      }
      return ''
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
/* Minimal vertical spacing utilities */
.py-25 {
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
}

.py-50 {
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
}

.pb-50 {
  padding-bottom: 0.5rem !important;
}

.mb-25 {
  margin-bottom: 0.25rem !important;
}

.mb-50 {
  margin-bottom: 0.5rem !important;
}

.mt-25 {
  margin-top: 0.25rem !important;
}

.mt-50 {
  margin-top: 0.5rem !important;
}

.mr-25 {
  margin-right: 0.25rem !important;
}

.mr-50 {
  margin-right: 0.5rem !important;
}

.mr-75 {
  margin-right: 0.75rem !important;
}

/* Form wrapper with distinct background color */
.expense-form-wrapper {
  border: 2px solid #7367f0;
  border-radius: 0.5rem;
  margin: 0.5rem;
  padding: 0.5rem !important;
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  box-shadow: 0 2px 4px rgba(115, 103, 240, 0.1);
}

/* Total expense display */
.total-expense-display {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(249, 67, 51, 0.728);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(234, 84, 85, 0.4);
  height: 100%;
}

.total-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  margin-right: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-amount {
  font-size: 1.125rem;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* Border danger for validation */
.border-danger {
  border-color: #ea5455 !important;
}

/* Compact table styling */
.table-sm td,
.table-sm th {
  padding: 0.3rem;
  vertical-align: middle;
}

.table-bordered {
  border: 1px solid #dee2e6;
}

.table-bordered td,
.table-bordered th {
  border: 1px solid #dee2e6;
}

/* Small form controls */
.form-control-sm {
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

/* v-select compact styling */
.expense-select-sm >>> .vs__dropdown-toggle {
  padding: 0;
  min-height: calc(1.5em + 0.5rem + 2px);
  border: 1px solid #d8d6de;
  background-color: #ffffff !important;
}

.expense-select-sm >>> .vs__search {
  padding: 0.25rem 0.5rem;
  margin: 0;
  font-size: 0.875rem;
  background-color: transparent;
}

.expense-select-sm >>> .vs__selected {
  margin: 0.125rem 0.25rem;
  padding: 0.125rem 0.25rem;
  font-size: 0.875rem;
  background-color: #f3f2f7;
}

.expense-select-sm >>> .vs__actions {
  padding: 0 0.25rem;
}

.expense-select-sm >>> .vs__dropdown-menu {
  background-color: #ffffff;
}

.expense-select-sm >>> .vs__clear {
  margin-right: 0;
}

/* Icon links styling */
a.text-info:hover {
  color: #0056b3 !important;
  text-decoration: none;
}

a.text-danger:hover {
  color: #c82333 !important;
  text-decoration: none;
}

/* Disabled link styling */
.disabled-link {
  pointer-events: none;
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive table */
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .expense-form-wrapper {
    margin: 0.25rem;
    padding: 0.5rem !important;
  }

  .justify-content-md-end {
    justify-content: flex-start !important;
  }
}
</style>
